import React, {useContext, useEffect, useState} from "react";
import axios from "../common/axios";
import Button from "../common/Button";
import ButtonsGroup from "../common/ButtonsGroup";
import AddEditClassDialog from "../schoolClasses/AddEditClassDialog";
import {UserContext} from "../common/UserContext";
import {BrandContext} from "../common/BrandContext";
import PagedList from "../common/PagedList";
import GenericDialog from "../common/GenericDialog";
import {Checkbox, Divider, Form} from "antd";
import CircularLoader from "../common/CircularLoader";
import {useBreadcrumbComponent} from "../common/useBreadcrumbComponent";
import useDynamicBreadcrumb from "../hooks/useDynamicBreadcrumb";
import AddStudentToClassDialog from "./components/AddStudentToClassDialog";

const ClassDetailsStudents = props => {
    const {classIdParam, callback, handleBack} = props;
    const classId = classIdParam;
    const BreadcrumbBar = useBreadcrumbComponent({dropEndingSeparator: true});
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [clazz, setClazz] = useState();
    const [allowPasswordReset, setAllowPasswordReset] = useState(false);
    const [teachersColumns, setTeachersColumns] = useState([]);
    const [teachersDataSource, setTeachersDataSource] = useState([]);
    const [studentsColumns, setStudentsColumns] = useState([]);
    const [studentsDataSource, setStudentsDataSource] = useState([]);
    const [students, setStudents] = useState([]);
    const [resetClassPasswords, setResetClassPasswords] = useState(false);
    const [resetClassPasswordForm] = Form.useForm();
    const [resetUserPassword, setResetUserPassword] = useState(null);
    const [resetUserPasswordForm] = Form.useForm();
    const [resetFeedback, setResetFeedback] = useState(null);
    const [resetError, setResetError] = useState(null);
    const [editClass, setEditClass] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [userResources, setUserResources] = useState([]);
    const [addStudentDialogVisible, setAddStudentDialogVisible] = useState(false);

    const targetBreadcrumbPath = `/web/ui/view/classes/schoolClasses/class/${classId}`;

    useDynamicBreadcrumb({
        label: clazz?.className,
        path: targetBreadcrumbPath
    });

    function loadClass(classId) {
        if (!classId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        axios.get(`/web/ui/myClasses/classDetails/${classId}`)
            .then(response => {
                setClazz(response.data.class);
                setAllowPasswordReset(response.data.allowPasswordReset);
                setStudentsColumns(buildColumns(response.data.allowPasswordReset));
                setStudentsDataSource(buildDataSource(response.data.students));
                setTeachersColumns(buildColumns(false));
                setTeachersDataSource(buildDataSource(response.data.teachers));
                setStudents(response.data.students);
                if (callback && typeof callback === 'function') callback();
            }).finally(() => {
                setLoading(false);
            });
    }

    function buildColumns(displayPasswordRest = false) {
        return [
            {title: 'Name/Username/Email', dataIndex: 'user', key: 'user', width: '50%', ellipsis: true},
            displayPasswordRest && {title: 'Password', dataIndex: 'password', key: 'password'},
            {title: 'Last login', dataIndex: 'login', key: 'login'},
            {title: 'Usage', dataIndex: 'usage', key: 'usage'},
            {title: 'Created', dataIndex: 'created', key: 'created'},
            {title: 'Expires', dataIndex: 'expires', key: 'expires'},
        ].filter(Boolean);
    }

    function buildDataSource(users) {
        return users.map(sourceUser => {
            return {
                key: sourceUser.userId,
                user: <>
                    {sourceUser.fullName}<br/>
                    <span className="ch-text-small"><span className="text-muted ch-text-b">Username:</span> {sourceUser.username}</span><br/>
                    <span className="ch-text-small"><span className="text-muted ch-text-b">Email:</span> {sourceUser.email}</span><br/>
                    {sourceUser.syllabuses.length > 0 && (
                        <div className="ch-text-small" style={{whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"}}>
                            <span className="text-muted ch-text-b">Resources: </span>
                            <a onClick={() => setUserResources(sourceUser.syllabuses)}>
                                {sourceUser.syllabuses.map(syllabus => syllabus.syllabusName).join(', ')}
                            </a>
                        </div>
                    )}
                </>,
                password: <Button onClick={() => setResetUserPassword(sourceUser)}>Reset</Button>,
                login: user.dateFormat.format(sourceUser.lastLoginDate),
                usage: (sourceUser.totalUsage.includes(':') ? sourceUser.totalUsage.replace(/\s+/g, '') : sourceUser.totalUsage),
                created: user.dateFormat.format(sourceUser.createDate),
                expires: user.dateFormat.format(sourceUser.expiryDate),
            }
        })
    }

    function printClass() {
        const url = '/usersCommon/printStudentList.action';
        const urlWithParams = url + `?classId=${classId}&reportFormat=PDF`;
        const printWindow = window.open(urlWithParams, 'studentListReport');
        printWindow.focus();
    }

    function handleDownloadClassPasswords(reportFormat) {
        window.open(`/usersCommon/downloadClassTemporaryPasswords.action?reportFormat=${reportFormat}&subscriberType=STUDENT`, '_blank');
    }

    useEffect(() => {
        if(!addStudentDialogVisible){
            loadClass(classId);
        }
    }, [classId, addStudentDialogVisible]);

    useEffect(() => {
        if (resetClassPasswords) setStudents(students.map(student => ({...student, selected: false})));
    }, [resetClassPasswords]);

    useEffect(() => {
        setResetFeedback(null);
        setResetError(null);
    }, [resetClassPasswords, resetClassPasswordForm, resetUserPassword, resetUserPasswordForm]);

    if(isLoading) {
        return <CircularLoader/>;
    }

    if (!clazz) {
        return <p className="">No class selected</p>;
    }

    return (
        <>
            <BreadcrumbBar />
            <Divider style={{margin: '8px 0'}} />
            <div className="flex flexAlignCentre flexSpaceBetween">
                    <h3 className="padding-r--m margin-y--rm">{clazz.className} Student and Subscription Details</h3>
                    {/*{clazz.classCode != null && (*/}
                    {/*    <h3 className="padding-r--m margin-y--rm">({clazz.classCode})</h3>*/}
                    {/*)}*/}
                    <ButtonsGroup className="flex">
                        {allowPasswordReset && (
                            <Button onClick={() => setResetClassPasswords(true)}>Reset Passwords</Button>
                        )}
                        {/*{user.teacher && user.schoolAdmin && (*/}
                        {/*    <Button onClick={() => setEditClass(true)}>Edit</Button>*/}
                        {/*)}*/}
                        <Button type={'outline'} onClick={() => setAddStudentDialogVisible(true)}>Add student</Button>
                        <Button onClick={() => printClass()}>Print</Button>
                    </ButtonsGroup>
                </div>
            {/*<table className="editTable displayValue" role="presentation">*/}
            {/*    <tbody>*/}
            {/*        <tr>*/}
            {/*            <td className="fieldTitle">Marking:</td>*/}
            {/*            <td>*/}
            {/*                {clazz.markingTypeDescription ? clazz.markingTypeDescription : '(none)'}*/}
            {/*            </td>*/}
            {/*        </tr>*/}
            {/*        <tr>*/}
            {/*            <td className="fieldTitle">Brand:</td>*/}
            {/*            <td>*/}
            {/*                {clazz.brandName ? clazz.brandName : '(none)'}*/}
            {/*            </td>*/}
            {/*        </tr>*/}
            {/*        {clazz.calendarYear != null && (*/}
            {/*            <tr>*/}
            {/*                <td className="fieldTitle">Calendar Year:</td>*/}
            {/*                <td>{clazz.calendarYear}</td>*/}
            {/*            </tr>*/}
            {/*        )}*/}
            {/*        {clazz.schoolYear != null && (*/}
            {/*            <tr>*/}
            {/*                <td className="fieldTitle">School year:</td>*/}
            {/*                <td>{clazz.schoolYearDescription}</td>*/}
            {/*            </tr>*/}
            {/*        )}*/}
            {/*        {clazz.syllabusGroupId != null && (*/}
            {/*            <tr>*/}
            {/*                <td className="fieldTitle">{brandConfig.syllabusGroupName}:</td>*/}
            {/*                <td>{clazz.syllabusGroupName}</td>*/}
            {/*            </tr>*/}
            {/*        )}*/}
            {/*    </tbody>*/}
            {/*</table>*/}
            <h3 className="margin-t--xl">Students</h3>
            <PagedList className="margin-t--l" columns={studentsColumns} dataSource={studentsDataSource} size={"small"} pagination={false} emptyText={"No students in class"}/>
            <h3 className="margin-t--xl">Teachers</h3>
            <PagedList className="margin-t--l" columns={teachersColumns} dataSource={teachersDataSource} size={"small"} pagination={false} emptyText={"No teachers in class"}/>
            <AddEditClassDialog clazz={clazz} open={editClass} setClose={() => setEditClass(false)} callback={() => loadClass(clazz.classId)}/>
            {user.brand.requireEmails === true && resetClassPasswords && (
                <GenericDialog
                    title={"Reset class passwords"}
                    contentStyle={{minWidth: 400}}
                    open={resetClassPasswords}
                    onClose={() => setResetClassPasswords(false)}
                    buttons={[
                        {name: 'Reset', callback: async () => {
                            axios.post('/web/ui/myClasses/sendClassResetPasswordEmail', {classId: clazz.classId})
                                .then(response => {
                                    if (response.data.success) {
                                        setResetFeedback(<>
                                            <p>Reset password emails have been sent to:</p>
                                            <p><span className="ch-text-b">{response.data.emailSentCount} users</span></p>
                                        </>);
                                    } else {
                                        displayToastMessage('Error:', response.data.message, 'error');
                                    }
                                })
                            }, disabled: resetFeedback},
                        {name: 'Cancel', extraProps: {type: 'text'}}
                    ]}>
                    <>
                        <p>Please confirm that you would like to send reset password emails for class <span className="ch-text-b">{clazz.className}</span>.</p>
                        {resetFeedback && (<div className="ch-emph-block margin-t--l padding--l">{resetFeedback}</div>)}
                    </>
                </GenericDialog>
            )}
            {user.brand.requireEmails == false && resetClassPasswords && (
                <GenericDialog
                    title={(resetFeedback === null ? 'Reset class passwords' : 'Class passwords')}
                    contentStyle={{minWidth: 400}}
                    open={resetClassPasswords}
                    onClose={() => setResetClassPasswords(false)}
                    errorMessage={resetError}
                    buttons={[
                        {name: 'Reset', callback: async () => {
                            setResetError(null);
                            const studentIds = (students.filter(student => student.selected)).map(student => student.userId);
                            if (studentIds.length === 0) {
                                setResetError(<>No students have been selected</>);
                            } else {
                                const params = {
                                    ...resetClassPasswordForm.getFieldsValue(),
                                    classId: clazz.classId,
                                    userIds: studentIds
                                }
                                axios.post('/web/ui/myClasses/resetClassPassword', params)
                                    .then(response => {
                                        if (response.data.success) {
                                            setResetFeedback(response.data.updatedStudents)
                                        } else {
                                            displayToastMessage('Error:', response.data.message, 'error');
                                        }
                                    })
                            }
                        }, disabled: resetFeedback, extraProps: {style: (resetFeedback === null ? {} : {display: 'none'})}},
                        {name: (resetFeedback === null ? 'Cancel' : 'Close'), extraProps: (resetFeedback === null ? {type: 'text'} : {})}
                    ]}>
                    <>
                        {resetFeedback === null ? (
                            <Form form={resetClassPasswordForm} initialValues={{sendEmail: false}}>
                                <p>Please select students from class <span className="ch-text-b">{clazz.className}</span> to reset passwords.</p>
                                <Form.Item name="sendEmail" valuePropName="checked">
                                    <Checkbox>Email passwords to users</Checkbox>
                                </Form.Item>
                                <PagedList className="margin-t--l" columns={[
                                    {title: 'Name', dataIndex: 'name', key: 'name'},
                                    {title: 'Username', dataIndex: 'username', key: 'username'},
                                    {title: 'Email', dataIndex: 'email', key: 'email'},
                                    {title: <Checkbox
                                            indeterminate={students.filter(student => student.selected).length > 0 && students.filter(student => student.selected).length < students.length}
                                            checked={students.filter(student => student.selected).length === students.length}
                                            onChange={event => {
                                                setStudents(students.map(student => ({...student, selected: event.target.checked})));
                                            }}
                                        />, dataIndex: 'check', key: 'check',
                                        render: (text, record) => (
                                            <Checkbox
                                                checked={record.selected}
                                                onChange={event => {
                                                    setStudents(students.map(student => student.userId === record.key ? { ...student, selected: event.target.checked } : student));
                                                }}
                                            />
                                        )},
                                ]} dataSource={
                                    students.map(student => {
                                        return {
                                            key: student.userId,
                                            name: (student.lastName + ", " + student.firstName),
                                            username: student.username,
                                            email: student.email,
                                            selected: student.selected
                                        }
                                    })
                                } size={"small"} pagination={false}/>
                                {resetFeedback && (<div className="ch-emph-block margin-t--l padding--l">{resetFeedback}</div>)}
                            </Form>
                        ) : (
                            <>
                                <div className="flex">
                                    <h3 className="flex padding-r--l">Download</h3>
                                    <ButtonsGroup className="flex">
                                        <Button onClick={() => handleDownloadClassPasswords('PDF')}>PDF</Button>
                                        <Button onClick={() => handleDownloadClassPasswords('XLS')}>Excel</Button>
                                    </ButtonsGroup>
                                </div>
                                <PagedList className="margin-t--l" columns={[
                                    {title: 'Name', dataIndex: 'name', key: 'name'},
                                    {title: 'Username', dataIndex: 'username', key: 'username'},
                                    {title: 'Password', dataIndex: 'password', key: 'password'},
                                ]} dataSource={
                                    resetFeedback.map(student => {
                                        return {
                                            key: student.userId,
                                            name: (student.lastName + ", " + student.firstName),
                                            username: student.username,
                                            password: student.password,
                                        }
                                    })
                                } size={"small"} pagination={false}/>
                            </>
                        )}
                    </>
                </GenericDialog>
            )}
            {user.brand.requireEmails == true && resetUserPassword && (
                <GenericDialog
                    title={"Send reset password email"}
                    contentStyle={{minWidth: 400}}
                    open={resetUserPassword !== null}
                    onClose={() => setResetUserPassword(null)}
                    buttons={[
                        {name: 'Reset', callback: async () => {
                            axios.post('/web/ui/myClasses/sendUserResetPasswordEmail', {userId: resetUserPassword.userId})
                                .then(response => {
                                    if (response.data.success) {
                                        setResetFeedback(<>
                                            <p>A reset password email has been sent to:</p>
                                            <p><span className="ch-text-b">{resetUserPassword.email}</span></p>
                                        </>);
                                    } else {
                                        displayToastMessage('Error:', '', 'error');
                                    }
                                })
                            }, disabled: resetFeedback},
                        {name: 'Close'}
                    ]}>
                    <>
                        <p>Please confirm that you would like to send a reset password email for:</p>
                        <table className="editTable displayValue" role="presentation">
                            <tbody>
                            <tr>
                                <td className="fieldTitle">User:</td>
                                <td>
                                    {resetUserPassword.fullName}
                                </td>
                            </tr>
                            <tr>
                                <td className="fieldTitle">Username:</td>
                                <td>
                                    {resetUserPassword.username}
                                </td>
                            </tr>
                            <tr>
                                <td className="fieldTitle">Email:</td>
                                <td>
                                    {resetUserPassword.email}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        {resetFeedback && (<div className="ch-emph-block margin-t--l padding--l">{resetFeedback}</div>)}
                    </>
                </GenericDialog>
            )}
            {user.brand.requireEmails == false && resetUserPassword && (
                <GenericDialog
                    title={"Reset user password"}
                    contentStyle={{minWidth: 400}}
                    open={resetUserPassword !== null}
                    onClose={() => setResetUserPassword(null)}
                    buttons={[
                        {name: 'Reset', callback: async () => {
                            const params = {
                                ...resetUserPasswordForm.getFieldsValue(),
                                userId: resetUserPassword.userId
                            }
                            axios.post('/web/ui/myClasses/resetUserPassword', params)
                                .then(response => {
                                    if (response.data.success) {
                                        setResetFeedback(<>
                                            <p>A temporary password has been created:</p>
                                            <p>Password: <span className="ch-text-b">{response.data.password}</span></p>
                                        </>);
                                    } else {
                                        displayToastMessage('Error:', response.data.message, 'error');
                                    }
                                })
                            }, disabled: resetFeedback},
                        {name: 'Close'}
                    ]}>
                    <Form form={resetUserPasswordForm} initialValues={{sendEmail: false}}>
                        <p>Please confirm that you would like to reset the password for:</p>
                        <table className="editTable displayValue" role="presentation">
                            <tbody>
                            <tr>
                                <td className="fieldTitle">User:</td>
                                <td>
                                    {resetUserPassword.fullName}
                                </td>
                            </tr>
                            <tr>
                                <td className="fieldTitle">Username:</td>
                                <td>
                                    {resetUserPassword.username}
                                </td>
                            </tr>
                            <tr>
                                <td className="fieldTitle">Email:</td>
                                <td>
                                    {resetUserPassword.email}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        <Form.Item name="sendEmail" valuePropName="checked">
                            <Checkbox>Email password to user</Checkbox>
                        </Form.Item>
                        <p>Clicking this box will display the new password for immediate use AND will email the password to the user.</p>
                        {resetFeedback && (<div className="ch-emph-block margin-t--l padding--l">{resetFeedback}</div>)}
                    </Form>
                </GenericDialog>
            )}
            <GenericDialog
                title={"Resources"}
                contentStyle={{minWidth: 400}}
                open={userResources.length > 0}
                onClose={() => setUserResources([])}
                buttons={[{name: 'Close'}]}>
                <>
                    <ul>
                        {userResources.map(resource => (
                            <li>{resource.syllabusName}</li>
                        ))}
                    </ul>
                </>
            </GenericDialog>
            <AddStudentToClassDialog
                selectedClass={clazz}
                visible={addStudentDialogVisible}
                onClose={() => setAddStudentDialogVisible(false)}
            />
        </>
    )
}

export default ClassDetailsStudents;